import React from 'react';
import moment from 'moment';
import { activityLocationParts } from './infoScreen2.utils';
import wIcon from './assets/info-screen-w-icon.png';

function AnalogClock({ currentTime, sizeClassName = '' }) {
	const hour = moment(currentTime).hour();
	const minute = moment(currentTime).minute();
	const second = moment(currentTime).second();
	return (
		<div className="clock-container">
			<div className={`clock ${sizeClassName}`.trim()}>
				<div className="clock-face">
					<div
						className="hand hour-hand"
						style={{ transform: `rotate(${(hour % 12) * 30 + minute * 0.5}deg)` }}
					/>
					<div
						className="hand minute-hand"
						style={{ transform: `rotate(${minute * 6}deg)` }}
					/>
					<div
						className="hand second-hand"
						style={{ transform: `rotate(${second * 6}deg)` }}
					/>
					<div className="center-dot" />
				</div>
			</div>
		</div>
	);
}

function TrilingualStack({ en, cn, vn }) {
	if (!en && !cn && !vn) return null;
	return (
		<span className="info-screen-3-trilingual">
			{en ? <span className="info-screen-3-en">{en}</span> : null}
			{cn ? <span className="info-screen-3-cn">{cn}</span> : null}
			{vn ? <span className="info-screen-3-vn">{vn}</span> : null}
		</span>
	);
}

function MealLangBlock({ en, cn, vn }) {
	const parts = [];
	if (cn) parts.push(<div key="cn" className="info-screen-3-cn">{cn}</div>);
	if (en) parts.push(<div key="en" className="info-screen-3-en">{en}</div>);
	if (vn) parts.push(<div key="vn" className="info-screen-3-vn">{vn}</div>);
	if (!parts.length) return <div className="info-screen-3-empty">&nbsp;</div>;
	return parts.reduce((acc, node, index) => {
		if (index > 0) {
			acc.push(<div key={`div-${index}`} className="info-screen-3-meal-lang-divider" />);
		}
		acc.push(node);
		return acc;
	}, []);
}

function MealSection({ barLabel, en, cn, vn }) {
	return (
		<div className="info-screen-3-meal-section">
			<div className="info-screen-3-meal-bar">{barLabel}</div>
			<div className="info-screen-3-meal-body">
				<MealLangBlock en={en} cn={cn} vn={vn} />
			</div>
		</div>
	);
}

const InfoScreen3Layout = ({
	isFullScreen,
	canViewAppointmentCalendar,
	medicalEvents,
	appointmentEventsCount,
	appointmentEmptyMessage = 'No medical appointments scheduled for today',
	appointmentTitleSuffix = '',
	appointmentPage,
	appointmentTotalPages,
	mealSections,
	activityPageEvents,
	activityEventsCount,
	activityEmptyMessage = 'No activities scheduled for today',
	currentTime,
	weatherPayload,
}) => {
	const appointments = canViewAppointmentCalendar ? (medicalEvents || []) : [];
	const activityRows = activityPageEvents || [];
	const layout = (
		<div className="info-screen-3-layout">
			<div className="info-screen-3-panel info-screen-3-clock">
				<AnalogClock currentTime={currentTime} sizeClassName={isFullScreen ? '' : 'clock-sm'} />
				<div className="info-screen-3-clock-text">
					<div className="info-screen-3-weekday">{moment(currentTime).format('dddd')}</div>
					<div className="info-screen-3-date">{moment(currentTime).format('D MMM, YYYY')}</div>
					<div className="info-screen-3-digital-time">{moment(currentTime).format('HH : mm')}</div>
				</div>
			</div>

			<div className="info-screen-3-panel info-screen-3-weather">
				<div className="weather-icon">
					<i className={weatherPayload?.weather?.iconClass || 'fas fa-cloud'} />
				</div>
				<div className="info-screen-3-clock-text">
					<div className="info-screen-3-weekday">
						{weatherPayload?.weather?.locationLabel || (weatherPayload?.zipcode ? `ZIP ${weatherPayload.zipcode}` : 'Weather location not set')}
					</div>
					<div className="info-screen-3-date">{weatherPayload?.weather?.condition || '—'}</div>
					<div className="info-screen-3-digital-time">
						{weatherPayload?.weather?.temperatureC != null
							? `${Math.round(weatherPayload.weather.temperatureC)}°C`
							: '—'}
					</div>
				</div>
			</div>

			<div className="info-screen-3-panel info-screen-3-brand">
				<img className="info-screen-3-w-icon" src={wIcon} alt="Worldshine" />
				<div className="info-screen-3-brand-text">
					<div className="info-screen-3-brand-cn">阳光每日时刻</div>
					<div className="info-screen-3-brand-en">Worldshine Daily Schedule</div>
					<div className="info-screen-3-brand-vn">Lịch trình hàng ngày của</div>
				</div>
			</div>

			<div className="info-screen-3-panel info-screen-3-appt">
				<div className="info-screen-3-panel-title">Appointment / 看诊 / Lịch khám{appointmentTitleSuffix}</div>
				<div className="info-screen-3-panel-body">
					{canViewAppointmentCalendar ? (
						<>
							<table className="info-screen-3-table">
								<thead>
									<tr>
										<th>Time</th>
										<th>Customer</th>
										<th>Driver</th>
									</tr>
								</thead>
								<tbody>
									{appointmentEventsCount > 0 ? (
										appointments.map((event) => (
											<tr key={event.id}>
												<td>{event.startTime24 || event.startTime}</td>
												<td>{event.customer}</td>
												<td>{event.driverName}</td>
											</tr>
										))
									) : (
										<tr>
											<td colSpan="3" className="info-screen-3-empty">{appointmentEmptyMessage}</td>
										</tr>
									)}
								</tbody>
							</table>
							{appointmentEventsCount > 0 && (
								<div className="info-screen-3-pager">
									Page {appointmentPage} / {appointmentTotalPages}
								</div>
							)}
						</>
					) : (
						<div className="info-screen-3-empty">Appointments not available</div>
					)}
				</div>
			</div>

			<div className="info-screen-3-panel info-screen-3-meal">
				<div className="info-screen-3-panel-title">Meal / 餐食 / Bữa ăn{appointmentTitleSuffix}</div>
				<div className="info-screen-3-panel-body info-screen-3-meal-stack">
					<MealSection
						barLabel="Breakfast / 早餐 / Bữa sáng"
						en={mealSections?.breakfast?.en}
						cn={mealSections?.breakfast?.cn}
						vn={mealSections?.breakfast?.vn}
					/>
					<MealSection
						barLabel="Lunch / 午餐 / Bữa trưa"
						en={mealSections?.lunch?.en}
						cn={mealSections?.lunch?.cn}
						vn={mealSections?.lunch?.vn}
					/>
				</div>
			</div>

			<div className="info-screen-3-panel info-screen-3-act">
				<div className="info-screen-3-panel-title">Activities / 活动 / Hoạt động{appointmentTitleSuffix}</div>
				<div className="info-screen-3-panel-body">
					<table className="info-screen-3-table">
						<thead>
							<tr>
								<th>Time</th>
								<th>Activities</th>
								<th>Location</th>
							</tr>
						</thead>
						<tbody>
							{activityEventsCount > 0 ? (
								activityRows.map((event, index) => {
									const location = activityLocationParts(event, { includeVn: true });
									return (
										<tr key={event.id || `activity-${index}`}>
											<td>{event.startTime24 || event.startTime}</td>
											<td>
												<TrilingualStack
													en={event.title}
													cn={event.title_cn}
													vn={event.title_vn}
												/>
											</td>
											<td>
												<TrilingualStack en={location.en} cn={location.cn} vn={location.vn} />
											</td>
										</tr>
									);
								})
							) : (
								<tr>
									<td colSpan="3" className="info-screen-3-empty">{activityEmptyMessage}</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);

	if (isFullScreen) return layout;

	return (
		<div className="info-screen-3-preview">
			{layout}
		</div>
	);
};

export default InfoScreen3Layout;
